/**
 * Debug Logger Utility
 *
 * Provides structured console logging with trace correlation support.
 * Production behavior: ALL logging OFF by default (except logger.error()).
 * Enable in production via: localStorage.setItem('debug', 'true')
 * Or enable specific loggers: localStorage.setItem('debug', 'ComponentName,OtherName')
 *
 * Usage:
 *   import { createLogger } from '@/utils/logger';
 *   const logger = createLogger('ComponentName');
 *   logger.debug('Message', { data });
 *   logger.info('Info message');
 *   logger.warn('Warning message');
 *   logger.error('Error message', error);  // Always logs
 *
 * Trace correlation:
 *   import { getTraceId, setFlow, checkpoint } from '@/utils/logger';
 *   setFlow('registration');
 *   checkpoint('User entered email');
 */

import { clampBytes, redactUuids } from '@/lib/redact';

// Generate a unique trace ID per session
const TRACE_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

// Store flow context
let currentFlow = 'init';
let flowStep = 0;

/** One entry of the log ring — the wire shape the error beacon carries. */
export interface LogEntry {
  t: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  ns: string;
  msg: string;
  data?: string;
}

/** How many entries the ring keeps — the browser console's recent tail, not
 *  its history. */
const RING_MAX = 50;
/** Per-field caps, in BYTES (the beacon's body cap counts bytes, so the ring
 *  bounds speak its unit): worst case 50 × (300 + 500 + ~120 of framing) ≈
 *  46 KiB, far under the cap. One pathological entry cannot fill the ring. */
const MSG_MAX_BYTES = 300;
const DATA_MAX_BYTES = 500;

/**
 * The ring: every logger call is ALSO kept here, bounded, regardless of the
 * console gate below — production keeps its quiet console and a reported
 * error still arrives with what the browser saw. POST /client-errors picks
 * the ring up with every beacon (recentLogEntries).
 */
const ring: LogEntry[] = [];

/** Serialize a structured payload for the ring: JSON when it can be, the
 *  string form when it cannot (a cyclic or exotic object), always bounded and
 *  masked. undefined stays undefined — an entry that logged no data carries
 *  none. */
function ringData(data: unknown): string | undefined {
  if (data === undefined) return undefined;
  let text: string;
  if (data instanceof Error) {
    text = `${data.name}: ${data.message}`;
  } else {
    try {
      text = JSON.stringify(data);
    } catch {
      text = String(data);
    }
  }
  return clampBytes(redactUuids(text), DATA_MAX_BYTES);
}

/** Record one entry in the ring — called on EVERY logger call, before the
 *  console gate: the ring is a report's evidence, and it must not depend on a
 *  debug switch having been flipped before the failure. */
function record(level: LogEntry['level'], ns: string, message: string, data?: unknown): void {
  const entry: LogEntry = {
    t: new Date().toISOString(),
    level,
    ns,
    msg: clampBytes(redactUuids(message), MSG_MAX_BYTES),
  };
  const serialized = ringData(data);
  if (serialized !== undefined) entry.data = serialized;
  ring.push(entry);
  if (ring.length > RING_MAX) ring.shift();
}

/** The ring's current contents, oldest first — a copy, never the ring. */
export function recentLogEntries(): LogEntry[] {
  return ring.slice();
}

/**
 * Get the current trace ID
 */
export function getTraceId(): string {
  return TRACE_ID;
}

/**
 * Set the current flow context
 */
export function setFlow(flow: string): void {
  currentFlow = flow;
  flowStep = 0;
  if (isDebugEnabled()) {
    console.log(`[TRACE:${TRACE_ID}] === Starting flow: ${flow} ===`);
  }
}

/**
 * Log a checkpoint (important state change)
 */
export function checkpoint(message: string): void {
  flowStep++;
  if (isDebugEnabled()) {
    console.log(`[TRACE:${TRACE_ID}] [${currentFlow}:${flowStep}] >>> CHECKPOINT: ${message} <<<`);
  }
}

/**
 * Check if debug mode is enabled
 * Production: OFF by default unless localStorage.debug is set
 * Development: ON by default
 */
function isDebugEnabled(namespace?: string): boolean {
  // Production mode - check localStorage
  if (import.meta.env.PROD) {
    try {
      const debug = localStorage.getItem('debug');
      if (!debug) return false;
      if (debug === 'true') return true;
      // Check if namespace is in comma-separated list
      if (namespace) {
        const enabledLoggers = debug.split(',').map((s) => s.trim());
        return enabledLoggers.includes(namespace);
      }
      return false;
    } catch {
      return false;
    }
  }

  // Development mode - enabled by default
  return true;
}

/**
 * Format timestamp for logs
 */
function formatTimestamp(): string {
  const now = new Date();
  return now.toISOString().split('T')[1].slice(0, -1); // HH:MM:SS.mmm
}

/**
 * Logger interface
 */
export interface Logger {
  debug(message: string, data?: unknown): void;
  info(message: string, data?: unknown): void;
  warn(message: string, data?: unknown): void;
  error(message: string, error?: unknown): void;
}

/**
 * Create a logger instance for a specific component/module
 */
export function createLogger(namespace: string): Logger {
  return {
    debug(message: string, data?: unknown): void {
      record('debug', namespace, message, data);
      if (!isDebugEnabled(namespace)) return;
      const prefix = `[${formatTimestamp()}] [${namespace}]`;

      if (data !== undefined) {
        console.log(`${prefix} ${message}`, data);
      } else {
        console.log(`${prefix} ${message}`);
      }
    },

    info(message: string, data?: unknown): void {
      record('info', namespace, message, data);
      if (!isDebugEnabled(namespace)) return;
      const prefix = `[${formatTimestamp()}] [${namespace}]`;

      if (data !== undefined) {
        console.info(`${prefix} ℹ️ ${message}`, data);
      } else {
        console.info(`${prefix} ℹ️ ${message}`);
      }
    },

    warn(message: string, data?: unknown): void {
      record('warn', namespace, message, data);
      if (!isDebugEnabled(namespace)) return;
      const prefix = `[${formatTimestamp()}] [${namespace}]`;

      if (data !== undefined) {
        console.warn(`${prefix} ⚠️ ${message}`, data);
      } else {
        console.warn(`${prefix} ⚠️ ${message}`);
      }
    },

    error(message: string, error?: unknown): void {
      record('error', namespace, message, error);
      // error() always logs, even in production
      const prefix = `[${formatTimestamp()}] [${namespace}]`;
      if (error !== undefined) {
        console.error(`${prefix} ❌ ${message}`, error);
      } else {
        console.error(`${prefix} ❌ ${message}`);
      }
    },
  };
}

// Log trace ID on initialization. Debug logging is toggled via
// localStorage: `localStorage.setItem('debug', 'true')` (or a
// comma-separated list of logger names), then reload.
if (isDebugEnabled()) {
  console.log(`[TRACE:${TRACE_ID}] === Session started at ${new Date().toISOString()} ===`);
  console.log(`[TRACE:${TRACE_ID}] User Agent: ${navigator.userAgent}`);
  console.log(`[TRACE:${TRACE_ID}] URL: ${window.location.href}`);
  console.log('%c🔍 Debug Mode Enabled', 'color: #0066CC; font-weight: bold; font-size: 14px;');
  console.log("%cTo disable: localStorage.removeItem('debug') and reload", 'color: #7A756D; font-style: italic;');
}
